<script setup lang="ts">
/**
 * A step bar (#1742): the steps of a guided flow in order, with the one the person is on marked. The current step
 * carries `aria-current="step"`, so a screen reader announces it; the steps before it read as done. Display only:
 * a step is moved by the screen that owns the flow, never by clicking the bar.
 */
import { computed } from 'vue'

export interface StepBarStep {
    id: string
    label: string
}

const props = defineProps<{
    steps: readonly StepBarStep[]
    /** The id of the step the person is on. */
    current: string
}>()

const currentIndex = computed(() => props.steps.findIndex(step => step.id === props.current))

function stateOf(index: number): 'done' | 'current' | 'todo' {
    if (index === currentIndex.value) {
        return 'current'
    }
    return currentIndex.value >= 0 && index < currentIndex.value ? 'done' : 'todo'
}
</script>

<template>
    <nav class="zh-step-bar" aria-label="Setup steps">
        <ol class="zh-step-bar__list">
            <li
                v-for="(step, index) in steps"
                :key="step.id"
                class="zh-step-bar__step"
                :class="`zh-step-bar__step--${stateOf(index)}`"
                :aria-current="stateOf(index) === 'current' ? 'step' : undefined"
            >
                {{ step.label }}
            </li>
        </ol>
    </nav>
</template>

<style scoped>
.zh-step-bar__list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin: 0;
    padding: 0;
    list-style: none;
}

.zh-step-bar__step {
    flex: 1 1 0;
    min-width: 6rem;
    padding: 0.5rem 0 0;
    border-top: 2px solid var(--border);
    font-family: var(--font-mono);
    font-size: 0.75rem;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
    color: var(--muted-grey);
}

.zh-step-bar__step--done {
    border-top-color: var(--muted-grey);
    color: var(--dirty-white);
}

.zh-step-bar__step--current {
    border-top-color: var(--electric-cyan);
    color: var(--electric-cyan);
}
</style>
